"use client"

import { useCallback, useSyncExternalStore } from "react"

/**
 * 站点 H5 断点 media query，与全局样式 `@media (max-width: 768px)` 保持一致。
 */
export const MOBILE_MEDIA_QUERY = "(max-width: 768px)"

/**
 * 订阅任意 CSS media query 是否匹配。
 *
 * 实现选用 `useSyncExternalStore`（而非组件内 `useEffect` + `setState`）：
 * - 把 matchMedia 当作外部 store，客户端首帧即可读到真实 `matches`，减少「先 PC 后 H5」闪一下；
 * - SSR 走 `getServerSnapshot`，与客户端订阅语义分离，避免在 effect 里再同步一遍。
 * 业务侧只需调用本 hook，不必各自写 matchMedia 监听。
 *
 * @param query - 合法 media query，如 `(max-width: 768px)`、`(min-width: 769px)`
 * @param serverFallback - SSR / 无 window 时的返回值，默认 `false`（按桌面端处理）
 * @returns 当前是否匹配；窗口变化时自动更新
 *
 * @example
 * const isNarrow = useMediaQuery("(max-width: 768px)");
 * const isPrint = useMediaQuery("print");
 */
export const useMediaQuery = (query: string, serverFallback = false): boolean => {
  // subscribe 须随 query 稳定，避免每次 render 重复订阅
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      const media = window.matchMedia(query)
      media.addEventListener("change", onStoreChange)
      return () => media.removeEventListener("change", onStoreChange)
    },
    [query]
  )

  const getSnapshot = useCallback(() => window.matchMedia(query).matches, [query])
  const getServerSnapshot = useCallback(() => serverFallback, [serverFallback])

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
}

/**
 * 是否为 H5 视口（`MOBILE_MEDIA_QUERY`）。
 *
 * 常见用法：Pagination `simple`、隐藏 PC 专属区块、切换 Drawer/Modal 等。
 * 若水合前不能渲染任一分支（防闪错 UI），请配合 `useHydrated`：
 * `if (!useHydrated()) return null;`
 *
 * @param serverFallback - SSR 默认按非 H5（`false`）；一般无需改
 */
export const useIsMobile = (serverFallback = false): boolean => useMediaQuery(MOBILE_MEDIA_QUERY, serverFallback)

/**
 * 是否已在客户端完成水合。
 *
 * - SSR / 服务端快照：始终 `false`
 * - 客户端：始终 `true`（无需监听，subscribe 为空即可）
 *
 * 适用：PC/H5 渲染完全不同的组件（如联系我们 Modal vs Drawer），
 * 水合前先 `return null`，避免首帧用错误分支再切换。
 * 仅需读断点、可接受 SSR 按桌面处理时，用 `useIsMobile` 即可，不必再包本 hook。
 */
export const useHydrated = (): boolean =>
  useSyncExternalStore(
    () => () => undefined,
    () => true,
    () => false
  )
